import { useCallback, useEffect, useRef, useState } from 'react';
import {
  connectSocket,
  disconnectSocket,
  isSocketConnected,
  onRenderUpdate,
  RenderUpdateEvent,
  subscribeToRoom,
  unsubscribeFromRoom,
} from '../lib/socket';

/**
 * Hook to manage socket connection lifecycle
 */
export function useSocketConnection() {
  const [connected, setConnected] = useState(isSocketConnected());

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = connectSocket(token);

    const handleConnect = () => setConnected(true);
    const handleDisconnect = () => setConnected(false);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Set initial state
    setConnected(socket.connected);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, []);

  return { connected };
}

/**
 * Hook to subscribe to a quote room and receive render updates
 */
export function useQuoteSocket(
  quoteId: string | undefined,
  onUpdate?: (data: RenderUpdateEvent) => void
) {
  const [connected, setConnected] = useState(isSocketConnected());
  const callbackRef = useRef(onUpdate);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (!quoteId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = connectSocket(token);
    const room = `quote:${quoteId}`;

    const handleConnect = () => {
      setConnected(true);
      subscribeToRoom(room);
    };

    const handleDisconnect = () => {
      setConnected(false);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Subscribe if already connected
    if (socket.connected) {
      subscribeToRoom(room);
      setConnected(true);
    }

    // Listen for render updates
    const cleanup = onRenderUpdate((data) => {
      // Only process updates for this quote
      if (data.quoteId === quoteId && callbackRef.current) {
        callbackRef.current(data);
      }
    });

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      unsubscribeFromRoom(room);
      cleanup();
    };
  }, [quoteId]);

  return { connected };
}

/**
 * Hook to listen for all render updates (for global header indicator)
 */
export function useGlobalRenderUpdates(onUpdate: (data: RenderUpdateEvent) => void) {
  const callbackRef = useRef(onUpdate);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    connectSocket(token);

    const cleanup = onRenderUpdate((data) => {
      callbackRef.current(data);
    });

    return cleanup;
  }, []);
}

/**
 * Hook to manage socket disconnect on logout
 */
export function useSocketCleanup() {
  const disconnect = useCallback(() => {
    disconnectSocket();
  }, []);

  return { disconnect };
}
